import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Group,
  Modal,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { FeatureFlagCommand } from "@/api/featureFlags";
import type { Capabilities } from "@/auth/rbac";
import { EnvBadge } from "@/components/EnvBanner";
import { env } from "@/config/env";
import type { AppPlatform, AppVersionPolicyDoc } from "@/firestore/appVersionPolicies";
import {
  isFeatureFlagMode,
  type FeatureFlagDoc,
  type FeatureFlagMode,
} from "@/firestore/featureFlags";
import { livePolicyFor } from "../app-versions/presentation";
import { classifyFeatureFlagChange, requiresSuperAdmin } from "./evaluation";
import {
  BUILD_FIELD_LABELS,
  MAX_BUILD,
  MAX_EXPECTED_BACK_DAYS,
  MAX_MESSAGE_LENGTH,
  MAX_REASON_LENGTH,
  MODE_OPTIONS,
  PLATFORMS,
  PLATFORM_LABELS,
  changeEffect,
  currentSnapshot,
  describeChange,
  flagActionError,
  flagCommand,
  flagPatch,
  noRowSnapshot,
  parseBuild,
  presetTargeting,
  resolveExpectedBackAt,
  resolvePlatform,
  reviewBuildTargeting,
  superAdminHint,
  toDateTimeLocal,
  toTargeting,
  validMessage,
  validReason,
  type FeatureFlagWrite,
  type PlatformForm,
  type PlatformResolution,
  type PlatformTargeting,
  type ReviewAndroid,
  type TargetingValues,
} from "./presentation";
import { FEATURE_FLAG_REGISTRY, type FeatureFlagKey } from "./registry";

export interface FeatureFlagDialogProps {
  flagKey: FeatureFlagKey;
  /** The live projection right now; null when the key has no row (or if it vanished mid-edit). */
  current: FeatureFlagDoc | null;
  /** `current.updatedAt` captured when the dialog opened (null when creating) — the stale guard. */
  expectedUpdatedAt: string | null;
  capabilities: Capabilities;
  pending: boolean;
  /** Live app version policies, shown as context for the review-build preset. */
  appVersions: readonly AppVersionPolicyDoc[];
  onSubmit(command: FeatureFlagCommand): Promise<unknown>;
  onClose(): void;
}

function toForm(value: PlatformTargeting): PlatformForm {
  return { mode: value.mode, min: value.min ?? "", max: value.max ?? "" };
}

function PlatformFields({
  platform,
  form,
  resolution,
  disabled,
  onChange,
}: {
  platform: AppPlatform;
  form: PlatformForm;
  resolution: PlatformResolution;
  disabled: boolean;
  onChange(form: PlatformForm): void;
}) {
  const label = PLATFORM_LABELS[platform];
  return (
    <Stack gap="xs">
      <NativeSelect
        label={`${label} availability`}
        value={form.mode}
        onChange={(event) =>
          onChange({ ...form, mode: event.currentTarget.value as FeatureFlagMode })
        }
        data={MODE_OPTIONS}
        disabled={disabled}
      />
      {form.mode === "builds" ? (
        <>
          <Group grow align="flex-start">
            <NumberInput
              label={`${label} minimum build`}
              description="Inclusive. Blank = no minimum."
              value={form.min}
              onChange={(value) => onChange({ ...form, min: value })}
              min={0}
              max={MAX_BUILD}
              allowDecimal={false}
              allowNegative={false}
              disabled={disabled}
              error={resolution.minError ?? undefined}
            />
            <NumberInput
              label={`${label} maximum build`}
              description="Inclusive. Blank = no maximum."
              value={form.max}
              onChange={(value) => onChange({ ...form, max: value })}
              min={0}
              max={MAX_BUILD}
              allowDecimal={false}
              allowNegative={false}
              disabled={disabled}
              error={resolution.maxError ?? undefined}
            />
          </Group>
          {resolution.rangeError ? (
            <Text size="xs" c="red">
              {resolution.rangeError}
            </Text>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}

/** "iOS: required 500 · recommended 505" for the review-build context line. */
function appVersionContext(docs: readonly AppVersionPolicyDoc[], platform: AppPlatform): string {
  const doc = livePolicyFor(docs, platform);
  const label = PLATFORM_LABELS[platform];
  return doc
    ? `${label}: required ${doc.requiredBuild} · recommended ${doc.recommendedBuild}`
    : `${label}: no policy`;
}

export function FeatureFlagDialog(props: FeatureFlagDialogProps) {
  const { flagKey, current, capabilities } = props;
  const entry = FEATURE_FLAG_REGISTRY[flagKey];
  // Snapshot what the operator reviewed; a live change is surfaced by the stale guard, never merged.
  const [baseline] = useState(() => current);
  const creating = baseline === null;
  // The superAdmin rule classifies against the server default (CONTRACT §3.1)…
  const before = currentSnapshot(baseline, entry.defaultAvailable);
  // …while the form and the summary start from what users see: for a create of
  // emailPasswordSignIn that is the apps' own default (on), not the server's (off).
  const shown = creating ? noRowSnapshot(flagKey) : before;
  const unknownMode =
    baseline !== null &&
    (!isFeatureFlagMode(baseline.iosMode) || !isFeatureFlagMode(baseline.androidMode));
  const [forms, setForms] = useState<Record<AppPlatform, PlatformForm>>(() => ({
    ios: {
      mode: isFeatureFlagMode(shown.iosMode) ? shown.iosMode : "off",
      min: shown.iosMinBuild ?? "",
      max: shown.iosMaxBuild ?? "",
    },
    android: {
      mode: isFeatureFlagMode(shown.androidMode) ? shown.androidMode : "off",
      min: shown.androidMinBuild ?? "",
      max: shown.androidMaxBuild ?? "",
    },
  }));
  const [message, setMessage] = useState(baseline?.message ?? "");
  const [backAtInput, setBackAtInput] = useState(() => toDateTimeLocal(before.expectedBackAt));
  const [backAtTouched, setBackAtTouched] = useState(false);
  const [reason, setReason] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewIosBuild, setReviewIosBuild] = useState<number | string>("");
  const [reviewAndroid, setReviewAndroid] = useState<"on" | "fromLive">("on");
  const [reviewAndroidLive, setReviewAndroidLive] = useState<number | string>("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const now = Date.now();
  const stale = (current?.updatedAt ?? null) !== props.expectedUpdatedAt;
  const busy = props.pending || submitting;
  const locked = busy || confirming;

  const resolutions: Record<AppPlatform, PlatformResolution> = {
    ios: resolvePlatform(forms.ios),
    android: resolvePlatform(forms.android),
  };
  // Untouched, the date stays exactly what the row holds (the input drops seconds).
  const backAt = backAtTouched
    ? resolveExpectedBackAt(backAtInput, now)
    : ({ kind: "value", value: before.expectedBackAt } as const);
  const messageOk = validMessage(message);
  const reasonOk = validReason(reason);
  const next: FeatureFlagWrite | null =
    resolutions.ios.value && resolutions.android.value && backAt.kind === "value" && messageOk
      ? {
          ...toTargeting({ ios: resolutions.ios.value, android: resolutions.android.value }),
          message: message.trim() || null,
          expectedBackAt: backAt.value,
        }
      : null;
  const change = next ? classifyFeatureFlagChange(before, next) : null;
  const needsSuperAdmin = change !== null && requiresSuperAdmin(entry.riskyDirection, change);
  const effect = next ? classifyFeatureFlagChange(shown, next) : null;
  const changed = creating || (next !== null && Object.keys(flagPatch(before, next)).length > 0);
  const valid = next !== null && reasonOk && changed;

  // Mirrors the server: the registry's risky direction, computed on availability, needs superAdmin.
  const disabledReason = !capabilities.act
    ? "Your role can't change feature flags."
    : needsSuperAdmin && !capabilities.superAdmin
      ? superAdminHint(flagKey, entry.riskyDirection)
      : env.isProd && !capabilities.actProd
        ? "Your role can't act in production."
        : null;
  const canSubmit = disabledReason === null && valid && !stale && !busy;

  const reviewIos = parseBuild(reviewIosBuild);
  const reviewLive = parseBuild(reviewAndroidLive);
  const reviewOk = reviewIos !== null && (reviewAndroid === "on" || reviewLive !== null);

  function applyTargeting(values: TargetingValues) {
    setForms({ ios: toForm(values.ios), android: toForm(values.android) });
  }

  function applyReviewBuild() {
    if (reviewIos === null) return;
    let android: ReviewAndroid;
    if (reviewAndroid === "on") android = { kind: "on" };
    else if (reviewLive !== null) android = { kind: "fromLive", liveBuild: reviewLive };
    else return;
    applyTargeting(reviewBuildTargeting(reviewIos, android));
    setReviewOpen(false);
  }

  async function submit() {
    if (!canSubmit || next === null) return;
    const command = flagCommand(flagKey, baseline, next, reason.trim());
    setSubmitting(true);
    setError(null);
    try {
      await props.onSubmit(command);
      props.onClose();
    } catch (e) {
      setError(flagActionError(e));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  }

  const submitLabel = creating ? "Create flag" : "Save flag";
  const submitButton = (
    <Button
      type="submit"
      color={needsSuperAdmin || env.isProd ? "red" : "blue"}
      disabled={!canSubmit}
      loading={busy}
    >
      {submitLabel}
    </Button>
  );
  const roleRule =
    entry.riskyDirection === "disable"
      ? "Any admin can turn it on for more builds or edit the copy; taking builds away needs the superAdmin role."
      : entry.riskyDirection === "enable"
        ? "Any admin can take builds away or edit the copy; turning it on for more builds needs the superAdmin role."
        : "Any admin can make this change.";

  return (
    <Modal
      opened
      title={`${creating ? "Create" : "Edit"} feature flag ${flagKey}`}
      onClose={() => {
        if (!busy) props.onClose();
      }}
      closeOnEscape={!busy}
      closeOnClickOutside={!busy}
      withCloseButton={!busy}
      size="lg"
      centered
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // Production: the first submit only opens the confirm step.
          if (env.isProd && !confirming) {
            if (canSubmit) setConfirming(true);
            return;
          }
          void submit();
        }}
      >
        <Stack>
          {env.isProd ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Group gap="xs">
                You are changing {flagKey} in <EnvBadge size="sm" />. Every device and the API
                follow within seconds.
              </Group>
            </Alert>
          ) : null}
          <Text size="sm" c="dimmed">
            {entry.description}{" "}
            {entry.appDefault
              ? `With no row the apps use their own default, ${entry.appDefault.label}; the server default (${entry.defaultAvailable ? "on" : "off"}) only feeds the superAdmin rule for the first write.`
              : `With no row the key uses its code default (${entry.defaultAvailable ? "on" : "off"} everywhere).`}{" "}
            Builds are whole numbers ({BUILD_FIELD_LABELS.ios} on iOS, {BUILD_FIELD_LABELS.android}{" "}
            on Android).
          </Text>
          {creating && entry.appDefault ? (
            <Alert
              color="yellow"
              variant="light"
              icon={<IconAlertTriangle size={16} />}
              title="This row replaces the apps' default"
            >
              Once it exists, every build on every bundle follows the row instead of the apps' own
              default ({entry.appDefault.label}). {entry.appDefault.offImpact}
              {entry.appDefault.available !== null
                ? " The form starts from what users see today."
                : null}
            </Alert>
          ) : null}
          {unknownMode ? (
            <Alert color="orange" variant="light">
              This row uses a mode this console does not know; it behaves as off. Saving replaces it
              with the mode selected below.
            </Alert>
          ) : null}
          <Group gap="xs">
            <Text size="sm" fw={500}>
              Presets
            </Text>
            <Button
              size="xs"
              variant="light"
              color="red"
              disabled={locked}
              onClick={() => applyTargeting(presetTargeting("disable"))}
            >
              Disable everywhere (maintenance)
            </Button>
            <Button
              size="xs"
              variant="light"
              color="teal"
              disabled={locked}
              onClick={() => applyTargeting(presetTargeting("enable"))}
            >
              Enable everywhere
            </Button>
            <Button
              size="xs"
              variant={reviewOpen ? "filled" : "light"}
              disabled={locked}
              onClick={() => setReviewOpen((open) => !open)}
            >
              Review build…
            </Button>
          </Group>
          {reviewOpen ? (
            <Card withBorder radius="md" padding="sm">
              <Stack gap="xs">
                <Text size="sm">
                  iOS gets exactly the build in review; Android stays on, or on from the live build
                  up, so the published app keeps it (Play app-access policy).
                </Text>
                <Text size="xs" c="dimmed">
                  Current app version policies — {appVersionContext(props.appVersions, "ios")};{" "}
                  {appVersionContext(props.appVersions, "android")}. Copy the build numbers from App
                  Store Connect and the Play Console, not from git (Android versionCode can
                  diverge).
                </Text>
                <Group grow align="flex-start">
                  <NumberInput
                    label="iOS build in review"
                    value={reviewIosBuild}
                    onChange={setReviewIosBuild}
                    min={0}
                    max={MAX_BUILD}
                    allowDecimal={false}
                    allowNegative={false}
                    disabled={locked}
                  />
                  <NativeSelect
                    label="Android"
                    value={reviewAndroid}
                    onChange={(event) =>
                      setReviewAndroid(event.currentTarget.value as "on" | "fromLive")
                    }
                    data={[
                      { value: "on", label: "On — every build" },
                      { value: "fromLive", label: "From the live build up" },
                    ]}
                    disabled={locked}
                  />
                  {reviewAndroid === "fromLive" ? (
                    <NumberInput
                      label="Android live build"
                      value={reviewAndroidLive}
                      onChange={setReviewAndroidLive}
                      min={0}
                      max={MAX_BUILD}
                      allowDecimal={false}
                      allowNegative={false}
                      disabled={locked}
                    />
                  ) : null}
                </Group>
                <Group justify="flex-end">
                  <Button size="xs" disabled={locked || !reviewOk} onClick={applyReviewBuild}>
                    Apply review build
                  </Button>
                </Group>
              </Stack>
            </Card>
          ) : null}
          {PLATFORMS.map((platform) => (
            <PlatformFields
              key={platform}
              platform={platform}
              form={forms[platform]}
              resolution={resolutions[platform]}
              disabled={locked}
              onChange={(form) => setForms((prev) => ({ ...prev, [platform]: form }))}
            />
          ))}
          <Textarea
            label="Message shown to users (optional)"
            description={`Public — anyone can read it, even before sign-in. Plain text, shown while the feature is unavailable. ${message.trim().length}/${MAX_MESSAGE_LENGTH}`}
            value={message}
            onChange={(event) => setMessage(event.currentTarget.value)}
            disabled={locked}
            minRows={2}
            autosize
            error={messageOk ? undefined : `At most ${MAX_MESSAGE_LENGTH} characters.`}
          />
          <TextInput
            type="datetime-local"
            label="Expected back (optional)"
            description={`Your local time, at most ${MAX_EXPECTED_BACK_DAYS} days ahead. Display only — nothing switches back on automatically. Blank clears it.`}
            value={backAtInput}
            onChange={(event) => {
              setBackAtInput(event.currentTarget.value);
              setBackAtTouched(true);
            }}
            disabled={locked}
            error={backAt.kind === "invalid" ? backAt.reason : undefined}
          />
          <Textarea
            label="Reason (required)"
            description={`Recorded for audit against your operator account; never shown to users. ${reason.trim().length}/${MAX_REASON_LENGTH}`}
            placeholder="Why is this flag changing?"
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            disabled={locked}
            minRows={2}
            autosize
            required
            error={
              reason.length > 0 && !reasonOk ? `1 to ${MAX_REASON_LENGTH} characters.` : undefined
            }
          />
          {next ? (
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                {describeChange(flagKey, shown, next, creating)}
              </Text>
              {effect && changeEffect(effect) ? (
                <Text size="sm" c={effect.disables ? "orange" : "teal"}>
                  {changeEffect(effect)}
                </Text>
              ) : null}
            </Stack>
          ) : null}
          {needsSuperAdmin && !capabilities.superAdmin ? (
            <Alert color="yellow" variant="light" title="Super-admin only">
              {superAdminHint(flagKey, entry.riskyDirection)}
            </Alert>
          ) : null}
          {stale ? (
            <Alert color="orange">
              This flag changed while you were editing. Close the dialog, review the latest values,
              and reopen it.
            </Alert>
          ) : null}
          {error ? (
            <Alert color="red">
              <Text size="sm">{error}</Text>
            </Alert>
          ) : null}
          <Text size="xs" c="dimmed">
            Takes effect on devices and the API within seconds. {roleRule}
          </Text>
          {confirming ? (
            <Alert color="red" variant="light" title="Confirm the production change">
              <Stack gap="xs">
                <Text size="sm">
                  {next ? describeChange(flagKey, shown, next, creating) : null} Real users see this
                  within seconds.
                </Text>
                <Group justify="flex-end">
                  <Button variant="default" disabled={busy} onClick={() => setConfirming(false)}>
                    Back
                  </Button>
                  <Button type="submit" color="red" disabled={!canSubmit} loading={busy}>
                    Confirm and save
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : (
            <Group justify="flex-end">
              <Button variant="default" disabled={busy} onClick={props.onClose}>
                Cancel
              </Button>
              {disabledReason ? (
                <Tooltip label={disabledReason} multiline w={320} withArrow>
                  <span>{submitButton}</span>
                </Tooltip>
              ) : (
                submitButton
              )}
            </Group>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
